<template>
	<div class="pipeline-card" :type="type" @click="handleClick($event)">
		<div class="pipeline-card-header">
			<!-- @slot Used to display pipeline card header -->
			<slot name="header" />
		</div>
		<div class="pipeline-card-body">
			<!-- @slot Used to display pipeline card content <br/> e.g. LabelValue component is used display key value data   -->
			<slot />
		</div>
		<div class="pipeline-card-footer">
			<!-- @slot Used to display pipeline card footer <br/> e.g. PipelineNumberStat and AlarmCircle is used display big number stats and alarm -->
			<slot name="footer" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * Pipeline widget big number view allows a user to visualise the pipeline data in big number format. The dashboard allows a user to switch between different representations like pipeline view, table view and big number view.
 */
export default defineComponent({
	name: "PipelineCard",
	props: {
		/**
		 * 'success' | 'error' | 'inProgress' | 'warning' | 'disabled' | 'standby' | 'paused'
		 */
		type: {
			type: String,
			default: "inProgress",
			validator(value) {
				return [
					"success",
					"error",
					"inProgress",
					"warning",
					"disabled",
					"standby",
					"paused"
				].includes(value);
			}
		}
	},
	methods: {
		handleClick(e) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
@use "sass:map";

$background-color: var(--dashboard-element-light);

$types: (
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning),
	"inProgress": var(--dashboard-primary),
	"disabled": var(--dashboard-font-light),
	"standby": var(--dashboard-font-light),
	"paused": var(--dashboard-font-light)
);

$pipeline-card-colors: (
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient)
);

$hover: var(--dashboard-element-dark);
$focus: var(--dashboard-primary);

$min-width: 354px;
$max-width: 708px;

.pipeline-card {
	display: flex;
	flex-direction: column;
	// justify-content: space-between;
	background-color: $background-color !important;
	border-radius: 8px;
	// min-width: $min-width;
	// max-width: $max-width;
	position: relative;
	cursor: pointer;
	// justify-content: space-between;
	// min-height: 205px;
	height: 100%;
	width: 100%;

	@each $type, $color in $types {
		&[type="#{$type}"] {
			> .pipeline-card-header > .title {
				word-break: break-all;
				color: $color;
			}

			background: map.get($pipeline-card-colors, $type);
		}

		@if $type == "disabled" {
			&[type="#{$type}"] {
				.label-value-wrapper > .label,
				.label-value-wrapper > .value,
				> .pipeline-card-footer > .pipeline-number-stat > .value,
				> .pipeline-card-footer > .pipeline-number-stat > .label,
				> .pipeline-card-footer > .pipeline-number-stat > .separator {
					color: $color !important;
				}
			}
		}
	}

	> .pipeline-card-header {
		padding-left: 16px;
		padding-right: 16px;
		padding-top: 16px;
		padding-bottom: 12px;

		> .title {
			font-size: 18px;
			line-height: 18px;
			font-weight: 600;
		}
	}

	> .pipeline-card-body {
		padding-left: 16px;
		padding-right: 16px;
		display: flex;
		flex-direction: column;
		// gap: 8px;
		min-height: 54px;

		> * {
			margin: 4px 0px 4px 0px;
		}

		> :first-child {
			margin: 0px 0px 4px 0px;
		}

		> :last-child {
			margin: 4px 0px 0px 0px;
		}
	}
	> .pipeline-card-footer {
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
		padding-top: 16px;
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		height: fit-content;
		// min-height: 94px;
		margin-top: auto;
	}

	&:active,
	&:hover:not(:active) {
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: $hover;
			border-radius: 8px;
		}
	}

	&:active {
		&:before {
			opacity: 0.08;
			cursor: pointer;
		}
	}

	&:hover:not(:active) {
		&:before {
			opacity: 0.04;
		}
	}

	&:focus {
		box-shadow: 0 0 0 2px $focus;
		outline: none;
	}
}

.pipeline-card-container {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
	grid-gap: 12px;
	padding: 0px 8px 0px 8px;
	// padding: 24px;
}

@media (max-width: 575px) {
	.pipeline-card-container {
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	}
}
</style>
